import React from "react";
import usePageMeta from "../lib/usePageMeta";

const values = [
  ["British Based", "UK-based stock, UK-focused dispatch."],
  ["Science First", "Decisions led by documentation rigour, not marketing."],
  ["Precision by Design", "Clear strength, batch, and storage on every label."],
  ["Quality Assured", "Selected suppliers, batch documentation, COAs where available."],
  ["Transparent Supply", "Honest stock status. Out-of-stock products stay visible."],
  ["Research Use Only", "Strictly for laboratory research. No medical claims."],
];

export default function About() {
  usePageMeta("About — British Research Laboratory Supply", "Built for British research supply. Documentation rigour, UK dispatch, for research use only.");
  return (
    <div className="relative" data-testid="about-page">
      {/* Subtle UK grid background */}
      <div className="absolute inset-0 -z-10 pg-grid-bg opacity-40" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <div className="pg-micro mb-3">// About Peptigen Labs</div>
            <h1 className="text-[#F7FAFC] text-4xl lg:text-5xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
              Built for British research supply.
            </h1>
            <p className="text-[#9CA8B8] mt-5 leading-relaxed">
              Peptigen Labs was created to bring a more rigorous, transparent, and design-led
              standard to the UK research-supply market. Our focus is clear documentation,
              careful sourcing, premium labelling, and fast UK dispatch.
            </p>
            <p className="text-[#9CA8B8] mt-4 leading-relaxed">
              We supply researchers, university laboratories, and qualified scientific buyers
              across the United Kingdom with research-use-only laboratory materials — never
              therapeutic, clinical, or consumer-grade material.
            </p>
          </div>
          <div className="lg:col-span-5">
            <svg viewBox="0 0 400 400" className="w-full h-auto">
              <g stroke="#00C2D6" strokeWidth="0.6" fill="none" opacity="0.5">
                {Array.from({ length: 10 }, (_, i) => (
                  <line key={`h${i}`} x1="0" y1={i * 40} x2="400" y2={i * 40} />
                ))}
                {Array.from({ length: 10 }, (_, i) => (
                  <line key={`v${i}`} x1={i * 40} y1="0" x2={i * 40} y2="400" />
                ))}
              </g>
              {/* Abstract UK outline */}
              <path
                d="M120 60 L150 80 L160 120 L150 160 L170 200 L190 220 L180 250 L210 280 L240 290 L260 320 L240 350 L200 360 L170 340 L150 310 L130 320 L100 300 L90 260 L70 230 L80 200 L70 160 L90 130 L100 100 Z"
                fill="rgba(0,194,214,0.06)"
                stroke="#00C2D6"
                strokeWidth="1.2"
              />
              <circle cx="200" cy="220" r="6" fill="#A6FF00" className="pg-pulse-glow" />
              <text x="200" y="380" textAnchor="middle" fill="#9CA8B8" fontSize="10" fontFamily="monospace" letterSpacing="3">
                UK · BASED · DISPATCH
              </text>
            </svg>
          </div>
        </div>

        <div className="mb-12">
          <div className="pg-micro mb-3">// One Company, One Website</div>
          <div className="pg-card p-6 border-l-2 border-l-[#A6FF00]" data-testid="brand-distinction-notice">
            <h2 className="text-[#F7FAFC] text-xl pg-heading mb-3" style={{ fontFamily: "'Rajdhani'" }}>
              Not affiliated with similarly named companies
            </h2>
            <p className="text-[#9CA8B8] text-sm leading-relaxed">
              Peptigen Labs Ltd is an independent company registered in England &amp; Wales, trading
              exclusively through <span className="text-[#F7FAFC]">peptigenlabs.co.uk</span>. We have no
              affiliation, shared ownership or connection with any other business, brand, website or
              social-media account using a similar name — in the UK or elsewhere. Reviews, products or
              customer experiences relating to other companies do not reflect Peptigen Labs Ltd. If you
              are unsure whether a website or seller is genuinely us, contact{" "}
              <a href="mailto:info@peptigenlabs.co.uk" className="text-[#00C2D6] hover:underline">info@peptigenlabs.co.uk</a>{" "}
              before ordering.
            </p>
          </div>
        </div>

        <div className="mb-12">
          <div className="pg-micro mb-3">// Values</div>
          <h2 className="text-[#F7FAFC] text-3xl pg-display mb-8" style={{ fontFamily: "'Exo 2'" }}>
            What we stand for
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {values.map(([title, body]) => (
              <div key={title} className="pg-card p-6">
                <div className="text-[#A6FF00] pg-heading text-lg mb-2" style={{ fontFamily: "'Rajdhani'" }}>
                  {title}
                </div>
                <div className="text-[#9CA8B8] text-sm leading-relaxed">{body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
